import type { ReportKeyMetricData } from "@repo/app-types/entities/report";
import { skeletonStyle } from "@repo/ui/Skeletons/skeletons";
import { DateTime } from "luxon";
import { useId, type FC, type ReactNode } from "react";
import {
  Area,
  AreaChart,
  Dot,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipContentProps,
} from "recharts";
import { BaseCard } from "./baseCard.js";
import { BaseTooltipContent } from "./baseTooltipContent.js";
import { SparklineGradient } from "./sparklineGradient.js";
import {
  computeValueChange,
  renderPreviousValue,
  renderValueAsString,
} from "./values.js";

export interface KeyMetricCardProps {
  title: ReactNode;
  data?: ReportKeyMetricData;
  rechartSyncId?: string;
}

export const KeyMetricCard: FC<KeyMetricCardProps> = (props) => {
  const { data } = props;

  const gradientId = useId();

  return (
    <BaseCard title={props.title}>
      {data ? (
        <>
          {data.value === null && data.sparkline.length === 0 ? (
            <div className="flex flex-auto items-center justify-center py-5 text-center text-sm text-gray-400">
              No data
            </div>
          ) : (
            <>
              <div className="flex items-end justify-between">
                <div className="text-3xl font-semibold text-foreground">
                  {renderValueAsString(data)}
                </div>
                <div className="text-sm font-semibold">
                  {computeValueChange(data).content}
                </div>
              </div>

              <div className="-m-[5px]">
                <AreaChart
                  syncId={props.rechartSyncId}
                  syncMethod="value"
                  style={{
                    width: "100%",
                    aspectRatio: "6 / 1",
                  }}
                  responsive
                  data={data.sparkline.map((value, index) => {
                    return {
                      date: DateTime.fromISO(data.minDate)
                        .plus({ days: index })
                        .toISODate(),
                      value,
                      valueType: data.valueType,
                    };
                  })}
                >
                  <SparklineGradient
                    id={gradientId}
                    color={computeValueChange(data).indication.sparklineColor}
                  />
                  <XAxis dataKey="date" hide />
                  <YAxis domain={["auto", "auto"]} hide />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke={computeValueChange(data).indication.sparklineColor}
                    strokeWidth={1.5}
                    fill={`url(#${gradientId})`}
                    dot={(props) => {
                      if (props.index % data.periodLengthDays !== 0) {
                        return null;
                      }

                      return (
                        <Dot
                          cx={props.cx}
                          cy={props.cy}
                          r={2}
                          stroke="none"
                          fill={
                            computeValueChange(data).indication.sparklineColor
                          }
                        />
                      );
                    }}
                  />
                  <Tooltip content={TooltipContent} />
                </AreaChart>
              </div>

              <div className="text-xs text-muted-foreground">
                {renderPreviousValue(data)}
              </div>
            </>
          )}
        </>
      ) : (
        <KeyMetricCardSkeleton />
      )}
    </BaseCard>
  );
};

const KeyMetricCardSkeleton: FC = () => (
  <div className="animate-pulse">
    <div className="flex items-end justify-between">
      <div className={skeletonStyle({ className: "h-8 w-16" })} />
      <div className={skeletonStyle({ className: "h-4 w-10" })} />
    </div>
    <div className="mt-3">
      <div
        className={skeletonStyle({ className: "h-[62px] w-full rounded-lg" })}
      />
    </div>
  </div>
);

const TooltipContent = (props: TooltipContentProps) => {
  const entry = props.payload.at(0);

  if (!props.active || !entry) {
    return null;
  }

  const data = entry.payload as {
    date: string;
    value: number | null;
    valueType: ReportKeyMetricData["valueType"];
  };

  return (
    <BaseTooltipContent
      label={
        <>{DateTime.fromISO(data.date).toLocaleString(DateTime.DATE_MED)}</>
      }
      entries={[
        {
          color: entry.color,
          value: renderValueAsString({
            value: data.value,
            valueType: data.valueType,
          }),
        },
      ]}
    />
  );
};
